import type { HttpPort, IAuthService, UserIdentity } from "../contracts";
import { mapUserIdentity } from "./mappers";
import type { SessionStore } from "./SessionStore";

// IAuthService over an HttpPort + SessionStore. IMPORTANT: the `http` injected here
// must be the BARE client (no refresh-on-401 wiring) to avoid a refresh loop — the
// main client's refresh callback delegates to AuthService.refresh() (see composition root).
export class AuthService implements IAuthService {
    constructor(
        private readonly http: HttpPort,
        private readonly store: SessionStore,
    ) {}

    async login(username: string, password: string, organizationId?: string): Promise<UserIdentity> {
        const res = await this.http.post<Record<string, unknown>>("/api/auth/login", {
            username,
            password,
            session_type: "webclient",
            ...(organizationId ? { organization_id: organizationId } : {}),
        });
        this.store.set({
            accessToken: String(res.access_token ?? ""),
            refreshToken: String(res.refresh_token ?? ""),
            expiresAt: typeof res.expires_in === "number" ? Date.now() + res.expires_in * 1000 : undefined,
        });
        return this.me();
    }

    async logout(): Promise<void> {
        const refreshToken = this.store.getRefreshToken();
        try {
            if (refreshToken) await this.http.post("/api/auth/logout", { refresh_token: refreshToken });
        } finally {
            this.store.clear();
        }
    }

    async me(): Promise<UserIdentity> {
        return mapUserIdentity(await this.http.get("/api/auth/me"));
    }

    async switchOrganization(organizationId: string): Promise<UserIdentity> {
        const res = await this.http.post<Record<string, unknown>>("/api/auth/switch-organization", {
            organization_id: organizationId,
        });
        this.store.patch({
            activeOrganizationId: organizationId,
            ...(typeof res.access_token === "string" ? { accessToken: res.access_token } : {}),
        });
        return this.me();
    }

    isAuthenticated(): boolean {
        return this.store.getAccessToken() != null;
    }

    // Wired into the main HttpClient as `refreshAccessToken`. Returns a fresh token or null.
    async refresh(): Promise<string | null> {
        const refreshToken = this.store.getRefreshToken();
        if (!refreshToken) return null;
        try {
            const res = await this.http.post<Record<string, unknown>>("/api/auth/refresh", {
                refresh_token: refreshToken,
            });
            const token = typeof res.access_token === "string" ? res.access_token : null;
            if (token) {
                this.store.patch({
                    accessToken: token,
                    refreshToken: typeof res.refresh_token === "string" ? res.refresh_token : refreshToken,
                });
            }
            return token;
        } catch {
            this.store.clear();
            return null;
        }
    }
}
